class SizeVariantPicker extends HTMLElement {
  #host = null;
  #variants = [];
  #options = [];
  #selectedValueEl = null;
  #optionPosition = 0;
  #optionKey = 'option1';

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.#host = this.closest('main-product');
    this.#optionPosition = parseInt(this.dataset.optionPosition, 10) || 0;
    this.#optionKey = `option${this.#optionPosition + 1}`;

    this.#variants = this.#parseVariants();
    this.#options = Array.from(this.querySelectorAll('.size-picker__option'));
    this.#selectedValueEl = this.querySelector('[data-selected-value]');

    this.#options.forEach((opt) => {
      opt.addEventListener('click', this.#onOptionClick);
      opt.addEventListener('keydown', this.#onOptionKeyDown);
    });

    this.#host?.addEventListener('pdp:variant-update', this.#onVariantUpdate);

    // Filter sizes to the initially-selected colour (the update event only
    // fires on change, so the first paint needs an explicit sync).
    const initialVariant = this.#getCurrentVariant();
    if (initialVariant) this.#syncSizesToColour(initialVariant);
  }

  disconnectedCallback() {
    this.#options.forEach((opt) => {
      opt.removeEventListener('click', this.#onOptionClick);
      opt.removeEventListener('keydown', this.#onOptionKeyDown);
    });
    this.#host?.removeEventListener('pdp:variant-update', this.#onVariantUpdate);
  }

  #parseVariants() {
    const node = this.closest('main-product')?.querySelector('[data-variants-data]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.error('size-picker: variants JSON parse failed', err);
      return [];
    }
  }

  #onOptionClick = (event) => {
    const opt = event.currentTarget;
    const newValue = opt.dataset.value;
    if (!newValue || opt.classList.contains('is-selected')) return;
    this.#selectValue(newValue);
  };

  #onOptionKeyDown = (event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.click();
      return;
    }

    // Roving tabindex skips sizes hidden for the current colour.
    const visible = this.#options.filter((opt) => !opt.hidden);
    const idx = visible.indexOf(event.currentTarget);
    if (idx === -1) return;

    let nextIdx = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIdx = (idx + 1) % visible.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIdx = (idx - 1 + visible.length) % visible.length;
    }
    if (nextIdx === null) return;
    event.preventDefault();
    visible[nextIdx].focus();
  };

  #selectValue(newValue) {
    const currentVariant = this.#getCurrentVariant();
    const nextVariant = this.#findMatchingVariant(newValue, currentVariant);

    if (!nextVariant) {
      console.error(`size-picker: no variant matches ${this.#optionKey}=${newValue}`);
      return;
    }

    this.#options.forEach((opt) => {
      const matches = opt.dataset.value === newValue;
      opt.classList.toggle('is-selected', matches);
      opt.setAttribute('aria-checked', matches ? 'true' : 'false');
      opt.setAttribute('tabindex', matches ? '0' : '-1');
    });

    if (this.#selectedValueEl) {
      this.#selectedValueEl.textContent = newValue;
    }

    this.dispatchEvent(new CustomEvent('variant:changed', {
      bubbles: true,
      composed: true,
      detail: {
        variant: nextVariant,
        optionChanged: this.#optionKey,
      },
    }));
  }

  #onVariantUpdate = (event) => {
    const variant = event.detail?.variant;
    if (!variant) return;
    this.#syncSizesToColour(variant);
  };

  // Show only the sizes that exist for the current colour (= the incoming
  // variant's non-size options). Sizes with no variant for this colour are
  // hidden; existing-but-unavailable sizes keep their OOS strike.
  #syncSizesToColour(variant) {
    const sizeValue = variant[this.#optionKey];

    this.#options.forEach((opt) => {
      const match = this.#variants.find(
        (v) => v[this.#optionKey] === opt.dataset.value && this.#sameColour(v, variant)
      );

      if (!match) {
        opt.hidden = true;
        opt.classList.remove('is-selected');
        opt.setAttribute('aria-checked', 'false');
        opt.setAttribute('tabindex', '-1');
        return;
      }

      opt.hidden = false;
      opt.classList.toggle('is-oos', !match.available);
      const matches = opt.dataset.value === sizeValue;
      opt.classList.toggle('is-selected', matches);
      opt.setAttribute('aria-checked', matches ? 'true' : 'false');
      opt.setAttribute('tabindex', matches ? '0' : '-1');
    });

    if (this.#selectedValueEl && sizeValue) {
      this.#selectedValueEl.textContent = sizeValue;
    }
  }

  // Every option except this picker's size option must match (the "colour").
  #sameColour(a, b) {
    return ['option1', 'option2', 'option3'].every((key) =>
      key === this.#optionKey ? true : a[key] === b[key]
    );
  }

  #getCurrentVariant() {
    const hostVariant = this.closest('main-product')?.currentVariant;
    if (hostVariant) return hostVariant;
    // First paint: <main-product> may not be upgraded yet (block scripts run before
    // the section script), so host.currentVariant is undefined. Resolve from the URL.
    const urlId = parseInt(new URL(window.location.href).searchParams.get('variant'), 10);
    if (urlId) {
      const match = this.#variants.find((v) => v.id === urlId);
      if (match) return match;
    }
    return this.#variants.find((v) => v.available) || this.#variants[0] || null;
  }

  #findMatchingVariant(newValue, currentVariant) {
    if (!currentVariant) {
      return this.#variants.find((v) => v[this.#optionKey] === newValue) || null;
    }

    const wantedOptions = ['option1', 'option2', 'option3'].map((key) => {
      if (key === this.#optionKey) return newValue;
      return currentVariant[key];
    });

    const exact = this.#variants.find((v) =>
      v.option1 === wantedOptions[0] &&
      v.option2 === wantedOptions[1] &&
      v.option3 === wantedOptions[2]
    );
    // `exact` already covers OOS (no availability filter). NEVER fall back to a
    // different colour (silent option switch = wrong cart item).
    return exact || null;
  }
}

if (!customElements.get('size-variant-picker')) {
  customElements.define('size-variant-picker', SizeVariantPicker);
}
